// @ts-nocheck
import BadgeForTable from "components/shared-components/BadgeForTable";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import { ORDER_TYPE } from "constants/order";
import { useCheckRole, useListChildOrders, useRemoveOrderChild } from "hooks";
import UpdateRefCodeModal from "pages/ ScanTheRow/UpdateRefCode/UpdateRefCodeModal";
import RefCode from "pages/Orders/RefCode";
import {
  canShowAddReceiveBusChildButton,
  checkItemAcceptForUpdateExitsRefCode,
} from "pages/Orders/utils";
import { formatOrderCostDisplayLabel } from "helpers/orderCostHelper";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  UncontrolledTooltip,
} from "reactstrap";
import AddReceiveBusChildModal from "./AddReceiveBusChildModal";
import MappingUnknownRefCodeToOrderModal from "./MappingUnknownRefCodeToOrderModal";

/**
 * OrderDetailChild component - Displays and manages child orders of a parent order
 * Features:
 * - Shows list of child orders with pagination and search
 * - Displays unknown orders that need mapping
 * - Allows mapping unknown orders to parent orders
 * - Supports updating reference codes
 * - Handles order removal and viewing details
 *
 * @param {Object} props - Component props
 * @param {string|number} props.parent_id - ID of the parent order
 * @param {string} props.parent_code - Code of the parent order
 * @param {string} props.path - Base path for order detail URLs
 * @param {Array} props.orderStatusList - List of available order statuses
 * @param {Object} props.receive_bus_info - Information about scanned orders and unknown orders
 * @param {Object} props.receive_bus_notes - Information about receive bus notes
 * @param {Function} props.refetchDetailOrder - Function to refresh parent order details
 * @param {Object} ref - Forwarded ref from parent component
 */
const OrderDetailChild = React.forwardRef((props, ref) => {
  const {
    parent_id,
    parent_code,
    path,
    orderStatusList,
    receive_bus_info,
    receive_bus_notes,
    refetchDetailOrder,
    isReceiveBus,
    parentStatus,
    scanSessionId,
    autoOpenBarcode,
    onAutoOpenConsumed,
    onScanFocusLockChange,
  } = props;

  const { t } = useTranslation(["order"]);
  const updateRefCodeModalRef = useRef(null);
  const { isLogAdmin } = useCheckRole();

  const [columnsTable, setColumnsTable] = useState([]);
  const [children, setChildren] = useState([]);
  const [data, setData] = useState(null);
  const [isOpenMappingRefCodeModal, setIsOpenMappingRefCodeModal] =
    useState(false);
  const [isOpenAddReceiveBusChildModal, setIsOpenAddReceiveBusChildModal] =
    useState(false);
  const [addChildInitialBarcode, setAddChildInitialBarcode] = useState("");
  const [dataMappingRefCode, setDataMappingRefCode] = useState(null);

  const shouldShowAddReceiveBusChildButton = canShowAddReceiveBusChildButton({
    isReceiveBus,
    parentStatus,
  });

  const [orderChildParams, setOrderChildParams] = useState({
    sizePerPage: 10,
    page: 1,
    sort: "-checker_date",
    columnFilter: {},
    keywords: "",
    run: false,
    parent_id: null,
  });

  const unknownOrders = useMemo(
    () =>
      receive_bus_info?.child
        ?.filter(c => c?.is_unknown)
        ?.map((c, index) => ({
          ...c,
          index: index + 1,
        })) || [],
    [receive_bus_info]
  );
  const hasUnknownOrders = unknownOrders.length > 0;
  const scannedOrdersCount = receive_bus_info?.scan_child?.length || 0;
  const totalOrdersCount = data?.totalCount || 0;

  const onChildListSuccess = response => {
    const data = response?.items || [];
    setData(response);
    setChildren(
      data?.map(c => {
        return {
          ...c,
          runnerScanned: receive_bus_info?.scan_child?.includes(c?.id)
            ? true
            : false,
        };
      })
    );
  };

  const { isFetching: isLoadingChildListOrder, refetch: refetchListChild } =
    useListChildOrders(
      {
        params: orderChildParams,
      },
      onChildListSuccess
    );

  useEffect(() => {
    if (orderChildParams && orderChildParams.run) {
      refetchListChild();
    }
  }, [orderChildParams]);

  useEffect(() => {
    setColumnsTable(initColumns);
  }, [orderStatusList, receive_bus_info]);

  useEffect(() => {
    if (parent_id) {
      setOrderChildParams(prev => {
        return {
          ...prev,
          parent_id: parent_id,
          run: true,
        };
      });
    }
  }, [parent_id]);

  const getOrderStatusLabel = useCallback(
    status => {
      return orderStatusList?.find(
        orderStatus => status?.toString() === orderStatus.value
      )?.label;
    },
    [orderStatusList]
  );

  const openReceiveBusChildModal = useCallback(
    (barcode = "") => {
      if (!shouldShowAddReceiveBusChildButton) {
        return;
      }

      setAddChildInitialBarcode(String(barcode).trim());
      setIsOpenAddReceiveBusChildModal(true);
      onScanFocusLockChange?.(true);
    },
    [onScanFocusLockChange, shouldShowAddReceiveBusChildButton]
  );

  useEffect(() => {
    if (autoOpenBarcode === null || autoOpenBarcode === undefined) {
      return;
    }

    onAutoOpenConsumed?.();
    openReceiveBusChildModal(autoOpenBarcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenBarcode]);

  React.useImperativeHandle(ref, () => ({
    refreshChildrenList() {
      refetchListChild();
    },

    clearChildrenList() {
      setData(null);
      setChildren([]);
    },
  }));

  const initColumns = [
    {
      dataField: "checker_date",
      text: t("detail.order_child.checker_date"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      },
      formatter: (cell, row) => {
        if (cell) {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }).format(Number(cell) * 1000);
        } else {
          return "";
        }
      },
    },
    {
      dataField: "order_code",
      text: t("detail.order_child.order_code"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      },
      formatter: (cell, row) => {
        return (
          <>
            <span
              title="Xem đơn con"
              className="text-primary mx-2"
              style={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={() => {
                window.open(`/${path}/${row?.id}`, "_blank");
              }}
            >
              {cell}
            </span>
            <Button
              id={`order_code_copy_${row.id}`}
              size="sm"
              color=""
              className="bg-light"
              onClick={e => navigator.clipboard.writeText(cell)}
            >
              <i className="far fa-copy fa-md text-secondary" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`order_code_copy_${row.id}`}
            >
              Copy
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      dataField: "ref_code",
      text: "Mã tham chiếu",
      formatter: (cell, row) => {
        const isAllowUpdateRefCode = isLogAdmin;
        const isAllowUpdateExistRefCode =
          isLogAdmin && checkItemAcceptForUpdateExitsRefCode(row);

        return (
          <RefCode
            t={t}
            ref_code={cell}
            data={row}
            isAllowUpdateRefCode={isAllowUpdateRefCode}
            onUpdateRefCode={() =>
              updateRefCodeModalRef?.current?.openModal({
                orderId: row?.id,
                orderRefCode: row.ref_code,
              })
            }
            isAllowUpdateExistRefCode={isAllowUpdateExistRefCode}
            onUpdateExistRefCode={() =>
              updateRefCodeModalRef?.current?.openModal({
                orderId: row?.id,
                orderRefCode: row.ref_code,
                isUpdateExist: true,
              })
            }
          />
        );
      },
    },
    {
      dataField: "status",
      text: t("detail.order_child.status"),
      sort: true,
      formatter: cell => {
        return getOrderStatusLabel(cell);
      },
    },
    {
      dataField: "statusRunnerScan",
      text: "Trạng thái Runner",
      sort: true,
      hidden: !receive_bus_info,
      formatter: (cell, row) => {
        const isScanned = receive_bus_info?.scan_child?.includes(row?.id);
        return isScanned ? (
          <BadgeForTable badgeColor={"success"}>Đã quét kiện</BadgeForTable>
        ) : (
          <BadgeForTable badgeColor={"warning"}>Chưa quét kiện</BadgeForTable>
        );
      },
    },
    {
      dataField: "sender_name",
      text: t("detail.order_child.customer"),
      sort: true,
      formatter: (cell, row) => {
        return row.type === 1 ? row.reciever_name : row.sender_name;
      },
    },
    {
      dataField: "sale_name",
      text: t("detail.order_child.sale"),
      sort: true,
    },
    // {
    //   dataField: "timeslot_id",
    //   text: t("detail.order_child.timeslot_id"),
    //   sort: true,
    //   formatter: (cell, row) => {
    //     return row?.time_slot_name;
    //   },
    // },
    // {
    //   dataField: "action_date",
    //   text: "Ngày hẹn",
    //   sort: true,
    //   formatter: (cell, row) => {
    //     return row?.action_date
    //       ? moment(row?.action_date).format("DD/MM/YYYY")
    //       : "";
    //   },
    // },
    {
      dataField: "cost",
      text: "Chi phí",
      sort: true,
      formatter: (cell, row) => formatOrderCostDisplayLabel(row?.cost_value),
    }
  ];

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder }
  ) => {
    const columnFilter = orderChildParams.columnFilter || {};

    // @ts-ignore
    setOrderChildParams(prev => ({
      ...prev,
      sort: sortField ? sortOrder + sortField : orderChildParams.sort,
      columnFilter: columnFilter,
      page: page || 1,
      sizePerPage: sizePerPage || 10,
    }));
  };

  const onSearchAllHandle = value => {
    setOrderChildParams(prev => ({
      ...prev,
      keywords: value,
    }));
  };

  const onSuccessRemoveOrderChild = () => {
    refetchListChild();
  };

  const { mutate: removeOrderChild, isLoading: isLoadingremoveOrderChild } =
    useRemoveOrderChild(onSuccessRemoveOrderChild);

  const listRowAction = [
    {
      icon: <i className="fas fa-trash fa-lg text-danger" />,
      text: "Xóa đơn con",
      // conditionVisible: {
      //   scanned: [false],
      //   runnerScanned: [false],
      // },
      onClick: row => {
        removeOrderChild({ orderChildId: row.id });
      },
    },
    {
      icon: <i className="fas fa-eye fa-lg text-primary ms-2" />,
      text: "Xem chi tiết",
      onClick: row => {
        switch (row.type) {
          case ORDER_TYPE.COLLECTOR.value:
            window.open(
              `/collection-order/${row.id}`,
              "_blank",
              "noopener,noreferrer"
            );
            break;
          default:
            window.open(
              `/manage-orders/${row.id}`,
              "_blank",
              "noopener,noreferrer"
            );
            break;
        }
      },
    },
  ];

  const columnsChildUnknown = [
    {
      dataField: "index",
      text: "STT",
      align: "center",
      headerStyle: (colum, colIndex) => {
        return { width: "80px", textAlign: "center" };
      },
      formatter: (cell, row) => {
        return cell;
      },
    },
    {
      dataField: "order_code",
      text: t("detail.order_child.order_code"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      },
      formatter: (cell, row) => {
        return (
          <>
            <span
              title=""
              className="text-primary mx-2"
              style={{ fontWeight: "bold", cursor: "pointer" }}
            >
              {cell}
            </span>
            <Button
              id={`order_code_copy_${row.id}`}
              size="sm"
              color=""
              className="bg-light"
              onClick={e => navigator.clipboard.writeText(cell)}
            >
              <i className="far fa-copy fa-md text-secondary" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`order_code_copy_${row.id}`}
            >
              Copy
            </UncontrolledTooltip>
          </>
        );
      },
    },

    {
      dataField: "ref_code",
      text: "Mã tham chiếu",
      formatter: (cell, row) => {
        const isAllowUpdateRefCode = false;
        const isAllowUpdateExistRefCode = false;
        return (
          <RefCode
            t={t}
            ref_code={cell}
            data={row}
            isAllowUpdateRefCode={isAllowUpdateRefCode}
            isAllowUpdateExistRefCode={isAllowUpdateExistRefCode}
          />
        );
      },
    },
    //   receive_bus_notes={
    //     "items": [
    //         {
    //             "key": "order_2503SOU00296",
    //             "note": "Ghi chú #1: Hàng dễ vỡ! Cần xử lý @ưu tiên & kiểm tra (100%)",
    //             "created_at": "2025-03-25 18:14:05",
    //             "is_unknown": 0,
    //             "child_ref_code": null,
    //             "child_order_code": "2503.SOU.00296"
    //         },
    //         {
    //             "key": "unknown_GS01761250059",
    //             "note": "Note #2: Cần kiểm tra nhiệt độ <= 25°C & độ ẩm >= 60% [Khẩn cấp]",
    //             "created_at": "2025-03-25 18:14:07",
    //             "is_unknown": 1,
    //             "child_ref_code": "GS01761250059",
    //             "child_order_code": null
    //         },
    //         {
    //             "key": "order_2503SOU00295",
    //             "note": "[Cập nhật] Ghi chú #3: Đã kiểm tra & xác nhận (OK) -> Chuyển giao ngay!",
    //             "created_at": "2025-03-25 18:14:12",
    //             "is_unknown": 0,
    //             "child_ref_code": null,
    //             "child_order_code": "2503.SOU.00295"
    //         },
    //         {
    //             "key": "unknown_GS01761250060",
    //             "note": "[Cập nhật] Note #4: Đã xử lý theo yêu cầu! Temp: 15°C & Humidity: 65% (Perfect) ✓",
    //             "created_at": "2025-03-25 18:14:14",
    //             "is_unknown": 1,
    //             "child_ref_code": "GS01761250060",
    //             "child_order_code": null
    //         }
    //     ],
    //     "total": 4,
    //     "last_updated": "2025-03-25 18:14:14"
    // }
    {
      dataField: "statusRunnerScan",
      text: "Trạng thái Runner",
      sort: true,
      formatter: (cell, row) => {
        return (
          <BadgeForTable badgeColor={"success"}>Đã quét kiện</BadgeForTable>
        );
      },
    },
    {
      dataField: "receive_bus_notes",
      text: "Ghi chú",
      headerStyle: (colum, colIndex) => {
        return { width: "600px" };
      },
      formatter: (cell, row) => {
        const info = receive_bus_notes?.items?.find(
          item => item.child_ref_code === row.ref_code
        );
        return (
          <div className="text-wrap" style={{ maxWidth: "600px" }}>
            {info?.note}
          </div>
        );
      },
    },
  ];

  const listRowActionChildUnknown = [
    {
      icon: <i className="fas fa-link fa-lg text-primary" />,
      text: "Mapping đơn",
      conditionVisible: {
        scanned: [false],
      },
      onClick: row => {
        handleToggleMappingModal();
        setDataMappingRefCode({
          ref_code: row?.ref_code,
          parent_id,
          parent_code,
        });
      },
    },
  ];

  const handleToggleMappingModal = () =>
    setIsOpenMappingRefCodeModal(prev => !prev);

  const handleMappingWithoutRefCode = useCallback(() => {
    handleToggleMappingModal();
    setDataMappingRefCode({
      parent_id,
      parent_code,
    });
  }, [parent_id, parent_code]);

  const handleMappingSuccess = useCallback(() => {
    refetchDetailOrder?.();
    setTimeout(refetchListChild, 1000);
  }, [refetchDetailOrder, refetchListChild]);

  const handleToggleAddReceiveBusChildModal = () => {
    if (!isOpenAddReceiveBusChildModal) {
      openReceiveBusChildModal("");
      return;
    }

    setAddChildInitialBarcode("");
    setIsOpenAddReceiveBusChildModal(false);
    onScanFocusLockChange?.(false);
  };

  useEffect(() => {
    return () => {
      onScanFocusLockChange?.(false);
    };
  }, [onScanFocusLockChange]);

  const handleAddReceiveBusChildSuccess = useCallback(() => {
    refetchDetailOrder?.();
    setTimeout(refetchListChild, 1000);
  }, [refetchDetailOrder, refetchListChild]);

  const renderOrderTitle = () => {
    const title = t("detail.order_child.title");
    if (receive_bus_info && totalOrdersCount > 0)
      return `${title} (Runner đã quét ${scannedOrdersCount}/${totalOrdersCount} kiện)`;
    return `${title} (${totalOrdersCount})`;
  };

  const renderUnknownOrdersTable = () => {
    if (!hasUnknownOrders) return null;

    return (
      <Card>
        <CardBody>
          <CardTitle className="d-flex gap-2 align-items-center">
            <span>
              {`${"Danh sách kiện chưa xác định Runner đã quét"} (${unknownOrders.length
                })`}
            </span>
            <Button
              color="primary"
              size="sm"
              onClick={handleMappingWithoutRefCode}
            >
              <i className="fas fa-link me-2" />
              Mapping đơn
            </Button>
          </CardTitle>
          <RemoteDataTable
            classNameTable="order-child-unknown"
            listRowAction={listRowActionChildUnknown}
            isShowSearchAll={false}
            isShowRowIndex={false}
            keyField="id"
            data={unknownOrders || []}
            isShowPagination={false}
            columns={columnsChildUnknown}
            loading={isLoadingChildListOrder}
            searchSticky={true}
          />
        </CardBody>
      </Card>
    );
  };

  return (
    <>
      <Card>
        <CardBody>
          <AddReceiveBusChildModal
            parentId={parent_id}
            scanSessionId={scanSessionId}
            initialBarcode={addChildInitialBarcode}
            isOpen={isOpenAddReceiveBusChildModal}
            toggle={handleToggleAddReceiveBusChildModal}
            onSuccessHandle={handleAddReceiveBusChildSuccess}
          />
          <MappingUnknownRefCodeToOrderModal
            dataMapping={dataMappingRefCode}
            isOpen={isOpenMappingRefCodeModal}
            toggle={handleToggleMappingModal}
            onSuccessMappingHandle={handleMappingSuccess}
          />
          <UpdateRefCodeModal
            ref={updateRefCodeModalRef}
            handleOnSuccessInsertOrderRefCode={refetchListChild}
          />
          <CardTitle>{renderOrderTitle()}</CardTitle>
          <RemoteDataTable
            classNameTable="order-child"
            listRowAction={listRowAction}
            isShowRowIndex={true}
            keyField="id"
            data={children}
            columns={columnsTable}
            page={data?.params?.page}
            sizePerPage={data?.params?.sizePerPage}
            totalSize={data?.totalCount}
            onTableChange={handleTableChange}
            onSearchAllHandle={onSearchAllHandle}
            loading={isLoadingChildListOrder}
            searchSticky={true}
          >
            {shouldShowAddReceiveBusChildButton ? (
              <Button
                type="button"
                color="primary"
                className="btn-label ms-2 flex-shrink-0"
                onClick={handleToggleAddReceiveBusChildModal}
              >
                <i className="fas fa-plus label-icon" />
                Thêm đơn con
              </Button>
            ) : null}
          </RemoteDataTable>
        </CardBody>
      </Card>
      {renderUnknownOrdersTable()}
    </>
  );
});

OrderDetailChild.propTypes = {
  parent_id: PropTypes.any,
  parent_code: PropTypes.any,
  orderStatusList: PropTypes.array,
  path: PropTypes.any,
  isReceiveBus: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  parentStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refetchDetailOrder: PropTypes.func,
  scanSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  autoOpenBarcode: PropTypes.string,
  onAutoOpenConsumed: PropTypes.func,
  onScanFocusLockChange: PropTypes.func,
};

export default OrderDetailChild;
